/**
 * Calculate total quantity selected for a product code across all passengers in a segment
 * Used to determine available stock considering all passengers' selections
 *
 * @param {Map} selectedProducts - Immutable Map of selected products
 * @param {string} segment - Segment: 'outgoing' or 'incoming'
 * @param {string} productCode - Product code to calculate total for
 * @returns {number} Total quantity selected
 */
export function calculateTotalQuantityByProductCode(selectedProducts, segment, productCode) {
  if (!selectedProducts || selectedProducts.size === 0) {
    return 0;
  }

  let totalQuantity = 0;
  selectedProducts.forEach((passengerProducts) => {
    const segmentProducts = passengerProducts.get(segment);
    if (segmentProducts && segmentProducts.size > 0) {
      const product = segmentProducts.find((p) => p.get('code') === productCode);
      if (product) {
        totalQuantity += product.get('quantity') || 0;
      }
    }
  });

  return totalQuantity;
}

/**
 * Calculate total quantities selected for all product codes across all passengers in a segment
 * Returns an object keyed by product code with total quantity selected
 * More efficient than calling calculateTotalQuantityByProductCode multiple times
 *
 * @param {Map} selectedProducts - Immutable Map of selected products
 * @param {string} segment - Segment: 'outgoing' or 'incoming'
 * @returns {Object} Object keyed by product code with total quantity selected
 *
 * @example
 * const totals = calculateAllTotalQuantitiesByProductCode(selectedProducts, 'outgoing');
 * // Returns: { 'BEB001': 5, 'SNK002': 3 }
 */
export function calculateAllTotalQuantitiesByProductCode(selectedProducts, segment) {
  const totalQuantitiesByCode = {};

  if (!selectedProducts || selectedProducts.size === 0) {
    return totalQuantitiesByCode;
  }

  selectedProducts.forEach((passengerProducts) => {
    const segmentProducts = passengerProducts.get(segment);
    if (segmentProducts && segmentProducts.size > 0) {
      segmentProducts.forEach((product) => {
        const code = product.get('code');
        const quantity = product.get('quantity') || 0;
        totalQuantitiesByCode[code] = (totalQuantitiesByCode[code] || 0) + quantity;
      });
    }
  });

  return totalQuantitiesByCode;
}
